import React, { useState } from "react";
import "./style.scss";
import Button from "components/BaseWidgets/button/index";
import FemaleEmptyState from "assets/images/png/empty-female.png";
import MaleEmptyState from "assets/images/png/empty-male.png";
import {
  WhiteTick,
  Favourite,
  Message,
  Cancel,
  ThreeDotMenu,
  WhitePremiumIcon,
  CloseIconPrimary,
} from "assets/icons";
import Tag from "components/BaseWidgets/tag";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { profileVerification } from "utils/profileVerification";
import {
  useAlbumRequestHandler,
  // useContactRequestHandler,
  useInterestRequestHandler,
  useMarkFavourite,
  useMarkNoInterested,
  useRequestAlbum,
  useSendInterest,
} from "hooks/api/useHome";
import { useQueryClient } from "@tanstack/react-query";
import Modal from "components/BaseWidgets/modal";
import ActionModal from "components/Settings/components/ActionModal";
import Dropdown from "components/BaseWidgets/dropdown";
import { DeleteIconCircle } from "assets/icons";
import { retrieveToken } from "utils/storage";
import { normalizeText } from "utils/formatHelper";
import { useRemoveFromProfile } from "hooks/api/useEngage";

// Added imports from first file for block, report, share modals // assuming shared NotInterestedModal import; adjust path if necessary
import ReportProfileModal from "../ProfileCard/components/ReportProfileModal";
import NotInterestedModal from "../ProfileCard/components/NotInterestedModal";
import ShareModal from "components/CustomWidgets/shareModal";
import { useBlockProfile, useReportProfile } from "hooks/api/useOnboard";
import Tooltip from "components/BaseWidgets/toolTip";
import AlertNotification from "components/CustomWidgets/alertNotification";
import {
  addDoc,
  collection,
  doc,
  serverTimestamp,
  setDoc,
} from "firebase/firestore";
import { db } from "./../../../firebase/firebaseChat";
import { usePremium } from "context/PremiumContext";
import GoPremiumModal from "components/CustomWidgets/goPremiumModal";
import { useSendMessage } from "hooks/api/useSendMessage";

function BasicProfileCard({
  snippetProfileId,
  buttonText,
  snippetImage,
  snippetProfileName,
  snippetProfileAge,
  snippetProfileHeight,
  snippetProfileJob,
  snippetPremium,
  snippetInterestSent,
  snippetVisible,
  snippetFav,
  snippetRecommended,
  snippetImageRequested,
  snipperVerification,
  isNotInterested,
  contactReqByMe,
  contactReqByOthers,
  photoReqByMe,
  photoReqByOthers,
  aec_id,
  InterestReqByOthers,
  cardClassName,
  shareUrl,
  profile,
  activeTab,
  selectedList,
  secondaryTabActive,
  tabVariant,
  ActionChange = () => {},
}) {
  const queryClient = useQueryClient();
  const { isPremium } = usePremium();
  const [showPremiumModal, setShowPremiumModal] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const [removeModal, setRemoveModal] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedProfileId, setSelectedProfileId] = useState(null);
  const [selectedProfileName, setSelectedProfileName] = useState("");
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isBlockModalOpen, setIsBlockModalOpen] = useState(false);
  const [isReportModalOpen, setIsReportModalOpen] = useState(false);

  const isImageRequestTab = tabVariant === "4";
  const isInterestedTab = tabVariant === "2";

  // For "Requests received" tab (activeTab "2")
  const isAcceptedTab = secondaryTabActive === "6";
  const isRejectedTab = secondaryTabActive === "7";
  
  // For "Requests by me" tab (activeTab "1")
  const isRequestsByMeAccepted = activeTab === "1" && secondaryTabActive === "2";
  const isRequestsByMeRejected = activeTab === "1" && secondaryTabActive === "3";
  
  const isCancelledTab = secondaryTabActive === "4";

  const shouldHideRequestButton =
    isAcceptedTab || 
    isRejectedTab || 
    isRequestsByMeAccepted ||
    isRequestsByMeRejected ||
    (isImageRequestTab && isCancelledTab);

  const userData = JSON.parse(retrieveToken("user") || "{}");
  const defaultImage =
    normalizeText(userData?.gender) === "male"
      ? FemaleEmptyState
      : MaleEmptyState;

  const { mutateAsync: sendMessage } = useSendMessage();

  const handleStartChat = async () => {
    const user = JSON.parse(retrieveToken("user") || "{}");
    const currentUserId = user?.aec_id;
    const messageText = `Hi, I am ${currentUserId}, I liked your profile and have sent you a connect request.`;
    const sorted = [currentUserId, aec_id].sort();

    const generatedChatId = `${sorted[0]}_${sorted[1]}`;

    try {
      /** 1️⃣ SEND TO BACKEND */
      const apiResponse = await sendMessage({
        id: aec_id, // receiver AEC ID
        message: messageText, // actual text
      });

      // console.log("API returned:", apiResponse);

      /** 2️⃣ WRITE TO FIRESTORE */
      const chatRef = doc(db, "chats", generatedChatId);

      await setDoc(
        chatRef,
        {
          lastMessage: messageText,
          lastMessageSender: currentUserId,
          lastMessageType: "text",
          lastMessageTime: serverTimestamp(),
        },
        { merge: true },
      );

      await addDoc(collection(chatRef, "messages"), {
        senderId: currentUserId,
        receiverId: aec_id,
        messageId: Date.now().toString(),
        text: messageText,
        type: "text",
        isDeleted: false,
        mediaUrl: null,
        status: "unread",
        timestamp: serverTimestamp(),
      });
    } catch (error) {
      console.error("❌ Error sending:", error);
    } finally {
      navigate("/connect/chats", {
        state: { chatId: generatedChatId, aec_id },
      });
    }
  };

  const invalidatePremiumMatches = () => {
    queryClient.invalidateQueries({ queryKey: ["premiumMatches"] });
    queryClient.invalidateQueries({ queryKey: ["recentlyViewed"] });
    queryClient.invalidateQueries({ queryKey: ["careerMatches"] });
    queryClient.invalidateQueries({ queryKey: ["professionalMatches"] });
    queryClient.invalidateQueries({ queryKey: ["profileMayLike"] });
    queryClient.invalidateQueries({ queryKey: ["recommendedProfiles"] });
    queryClient.invalidateQueries({ queryKey: ["latestProfiles"] });
    queryClient.invalidateQueries({ queryKey: ["nearMeMatches"] });
    queryClient.invalidateQueries({ queryKey: ["myMatches"] });
    queryClient.invalidateQueries({ queryKey: ["profileVisited"] });
    queryClient.invalidateQueries({ queryKey: ["profileInterested"] });
    queryClient.invalidateQueries({ queryKey: ["contactRequest"] });
    queryClient.invalidateQueries({ queryKey: ["imageRequest"] });
    queryClient.invalidateQueries({ queryKey: ["favorites"] });
    queryClient.invalidateQueries({ queryKey: ["similiarMatches"] });
    queryClient.invalidateQueries({ queryKey: ["profile-completion"] });
  };

  const { mutate: requestAlbumMutate } = useRequestAlbum({
    onSuccess: () => {
      invalidatePremiumMatches();
      ActionChange("image_requested", snippetProfileId);
    },
  });

  const { mutate: sendInterestMutate } = useSendInterest({
    onSuccess: () => {
      invalidatePremiumMatches();
      ActionChange("has_expressed_interest", snippetProfileId);
      queryClient.invalidateQueries({ queryKey: ["profileInterestedCount"] });
    },
  });

  const { mutate: markNoInterestMutate } = useMarkNoInterested({
    onSuccess: () => {
      setIsModalOpen(false);
      invalidatePremiumMatches();
      ActionChange("", snippetProfileId, "remove");
    },
  });

  const { mutate: markFavouriteMutate } = useMarkFavourite({
    onSuccess: () => {
      invalidatePremiumMatches();
      ActionChange("has_favorited", snippetProfileId);
    },
  });

  const { mutate: removeProfile } = useRemoveFromProfile({
    onSuccess: () => {
      setRemoveModal(false);
      invalidatePremiumMatches();
    },
  });

  const { mutate: albumRequestHandler } = useAlbumRequestHandler({
    onSuccess: () => {
      invalidatePremiumMatches();
      ActionChange("image_requested", snippetProfileId);
      queryClient.invalidateQueries({ queryKey: ["imageRequestCount"] });
    },
  });

  const { mutate: interestRequestHandler } = useInterestRequestHandler({
    onSuccess: () => {
      invalidatePremiumMatches();
      ActionChange("has_expressed_interest", snippetProfileId);
      queryClient.invalidateQueries({ queryKey: ["profileInterestedCount"] });
    },
  });

  // Added mutate hooks for block and report profiles
  const { mutate: blockProfileMutate } = useBlockProfile({
    onSuccess: () => {
      invalidatePremiumMatches();
      setIsBlockModalOpen(false);
      ActionChange("", snippetProfileId, "remove");
      // navigate(-1);
    },
  });

  const { mutate: reportProfileMutate } = useReportProfile({
    onSuccess: () => {
      invalidatePremiumMatches();
      setIsReportModalOpen(false);
      ActionChange("", snippetProfileId, "remove");
    },
  });

  // Menu items updated with handlers to open modals
  const menuItems = [
    {
      key: "share",
      label: "Share this Profile",
      onClick: () => setIsShareModalOpen(true),
    },
    {
      key: "block",
      label: "Block this Profile",
      onClick: () => setIsBlockModalOpen(true),
    },
    {
      key: "report",
      label: "Report this Profile",
      onClick: () => setIsReportModalOpen(true),
    },
  ];

  const handleCardClick = () => {
    const isEngage = location.pathname.includes("/engage");
    if (isEngage) {
      sessionStorage.setItem("engageActiveTab", tabVariant);
      sessionStorage.setItem("engageSubTab", activeTab || "");
      sessionStorage.setItem("engageSecondaryTab", secondaryTabActive || "");
    }

    const scrollKey = `scroll-${location.pathname}-tab-${tabVariant}`;
    sessionStorage.setItem(scrollKey, window.scrollY.toString());
    sessionStorage.setItem("profileListPath", location.pathname);

    if (isEngage) {
      // States already saved above
    } else {
      sessionStorage.setItem("exploreActiveTab", tabVariant);
      if (selectedList) {
        sessionStorage.setItem("exploreSelectedList", selectedList.toString());
      }
    }

    navigate(`/person-profile/${aec_id}`, {
      state: { fromProfileDetail: true },
    });
  };

  return (
    <>
      <div className={`profile-snippet ${cardClassName}`}>
        <div
          className={`profile-data ${snippetPremium ? "gradient-border" : "border"}`}
        >
          <Link onClick={handleCardClick}>
            <div
              className="profile-image"
              style={{
                width: "100%",
                height: "100%",
                backgroundImage: `url(${snippetImage || defaultImage})`,
                backgroundSize: "cover",
                backgroundPosition: "top center",
                backgroundRepeat: "no-repeat",
                borderRadius: "inherit",
              }}
            />
          </Link>

          <div className="profile-status">
            {snippetPremium && (
              <div className="premium-profile">
                <WhitePremiumIcon />
              </div>
            )}
            {!isNotInterested && (
              <div className="profile-review">
                {snippetInterestSent && (
                  <div className="review-box">
                    <WhiteTick stroke="#027269" />
                  </div>
                )}
                {snippetFav && (
                  <div className="review-box">
                    <Favourite fill="#AA1538" stroke="AA1538" />
                  </div>
                )}
              </div>
            )}
          </div>
          {snippetRecommended && (
            <div className="recommended-profile">
              <Tag
                variant="yellow"
                className="recommended-tag font-body-small fw-700"
              >
                Recommended
              </Tag>
            </div>
          )}
          {!shouldHideRequestButton &&
            (isNotInterested ||
              snippetVisible ||
              contactReqByMe ||
              contactReqByMe ||
              contactReqByOthers ||
              photoReqByMe ||
              photoReqByOthers ||
              InterestReqByOthers) && (
              <div
                className={`hidden-profile ${cardClassName}`}
                onClick={handleCardClick}
              >
                {photoReqByOthers || InterestReqByOthers ? (
                  <div className="flex flex-col gap-2">
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();
                        const payload = {
                          id: snippetProfileId,
                          status: 2,
                          // status = 2:Accepted , 3:Rejected , 4:Cancelled
                        };
                        if (InterestReqByOthers) {
                          interestRequestHandler(payload);
                          return;
                        }
                        albumRequestHandler(payload);
                      }}
                      className="request-btn"
                      variant="request"
                      wFull
                    >
                      Approve
                    </Button>
                    <Button
                      height={37}
                      onClick={(e) => {
                        e.stopPropagation();
                        const payload = {
                          id: snippetProfileId,
                          status: 3,
                          // status = 2:Accepted , 3:Rejected , 4:Cancelled
                        };
                        if (InterestReqByOthers) {
                          interestRequestHandler(payload);
                          return;
                        }
                        albumRequestHandler(payload);
                      }}
                      className="request-btn"
                      variant="secondary"
                      wFull
                    >
                      Reject
                    </Button>
                  </div>
                ) : (
                  !contactReqByMe &&
                  (profile?.is_private === true ||
                    profile?.isPrivate === true) &&
                  !shouldHideRequestButton && (
                    <Button
                      onClick={(e) => {
                        e.stopPropagation();

                        if (photoReqByMe || snippetImageRequested) {
                          const payload = {
                            id: snippetProfileId,
                            status: 4,
                          };
                          albumRequestHandler(payload);
                          return;
                        }
                        if (isNotInterested) {
                          setRemoveModal(true);
                          return;
                        }
                        requestAlbumMutate({
                          id: snippetProfileId,
                          name: snippetProfileName,
                        });
                      }}
                      className="request-btn"
                      variant="request"
                    >
                      {isCancelledTab
                        ? "Request photo"
                        : contactReqByMe ||
                            photoReqByMe ||
                            snippetImageRequested
                          ? "Cancel request"
                          : "Request photo"}
                    </Button>
                  )
                )}
              </div>
            )}
          {snippetVisible && (
            <div>
              <img
                src={snippetImage || defaultImage}
                alt={`${buttonText}-img`}
              />
            </div>
          )}
          <div className="profile-actions">
            <Tooltip title="Send Interest">
              <Button
                className={`hover-btn`}
                variant="round"
                onClick={() => {
                  const payload = {
                    id: snippetProfileId,
                    status: 4,
                    // status = 2:Accepted , 3:Rejected , 4:Cancelled
                  };
                  if (snippetInterestSent) {
                    interestRequestHandler(payload);
                    return;
                  }
                  sendInterestMutate({ id: snippetProfileId });
                }}
              >
                {!snippetInterestSent ? (
                  <WhiteTick stroke="#aad2cfff" />
                ) : (
                  <WhiteTick stroke="#027269" />
                )}
              </Button>
            </Tooltip>
            <Tooltip title="Favourite">
              <Button
                className={`hover-btn`}
                variant="round"
                onClick={() => markFavouriteMutate({ id: snippetProfileId })}
              >
                {snippetFav ? (
                  <Favourite fill="#AA1538" stroke="#AA1538" />
                ) : (
                  <Favourite stroke="#AA1538" />
                )}
              </Button>
            </Tooltip>
            <Tooltip title="Message">
              <Button
                className={`hover-btn`}
                variant="round"
                onClick={(e) => {
                  e.preventDefault();
                  if (!isPremium) {
                    setShowPremiumModal(true);
                    return;
                  }
                  handleStartChat();
                }}
              >
                <Message stroke="#7777DB" />
              </Button>
            </Tooltip>
            {!isNotInterested && (
              <Tooltip title="Not Interested">
                <Button
                  className={`hover-btn`}
                  variant="round"
                  onClick={() => {
                    setSelectedProfileId(snippetProfileId);
                    setSelectedProfileName(snippetProfileName);
                    setIsModalOpen(true);
                  }}
                >
                  <Cancel stroke="#AA5B15" />
                </Button>
              </Tooltip>
            )}
          </div>
        </div>
        <div className="profile-info flex gap-1-5 items-center justify-between">
          <div className="user-data ">
            <div className="flex gap-1-5 items-center">
              <span className="name-ellipsis font-title-xs fw-700 flex items-center">
                {snippetProfileName}
              </span>
              {profileVerification(snipperVerification)}
            </div>
            <p className="font-body-normal grey-shade flex items-center">
              {snippetProfileAge} yrs, {snippetProfileHeight},{" "}
              {snippetProfileJob}
            </p>
          </div>
          <div className="menu-wrapper">
            <span className="three-dot-menu">
              <Dropdown
                items={menuItems}
                trigger={["click"]}
                label={<ThreeDotMenu />}
              />
            </span>
          </div>
        </div>
      </div>

      {/* Modal to mark Not Interested */}
      <Modal
        isOpen={isModalOpen}
        onCancel={() => setIsModalOpen(false)}
        onOk={() => {
          markNoInterestMutate({ id: selectedProfileId });
        }}
        title="Are you sure?"
        footer={null}
        closable
        closeIcon={<CloseIconPrimary />}
        width={440}
      >
        <div className="font-body-large">
          Mark <span className="fw-700">{selectedProfileName}</span> as{" "}
          <span className="fw-700">"Not Interested"</span>?
        </div>
        <div className="flex justify-end gap-2 mt-4">
          <Button
            className="modal-buttons"
            variant="secondary"
            height={44}
            onClick={() => setIsModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            className="modal-buttons"
            onClick={() => {
              markNoInterestMutate({ id: selectedProfileId });
              setIsModalOpen(false);
            }}
          >
            Confirm
          </Button>
        </div>
      </Modal>

      {/* Remove profile confirmation modal */}
      <ActionModal
        title="Remove account"
        description={
          <>
            Are you sure to remove{" "}
            <span className="fw-700">{snippetProfileName}</span>
          </>
        }
        open={removeModal}
        setOpen={setRemoveModal}
        footer={{
          left: { label: "Cancel", action: () => setRemoveModal(false) },
          right: {
            label: "Confirm",
            action: () => removeProfile({ id: snippetProfileId }),
          },
        }}
        icon={<DeleteIconCircle />}
      />

      {/* Added Share modal */}
      <ShareModal
        open={isShareModalOpen}
        onCancel={() => setIsShareModalOpen(false)}
        defaultLink={shareUrl || `https://www./profile/${aec_id}`}
      />

      {/* Added Block modal */}
      <NotInterestedModal
        isOpen={isBlockModalOpen}
        onCancel={() => setIsBlockModalOpen(false)}
        onConfirm={() => {
          blockProfileMutate({
            user_id: snippetProfileId,
            reason: "Inappropriate content",
          });
          setIsBlockModalOpen(false);
        }}
        title="Block this Profile"
        description={
          <>
            once blocked, this person will not be able to contact you or view
            your profile
          </>
        }
        confirmText="Block"
        confirmVariant="danger"
      />

      {showPremiumModal && (
        <GoPremiumModal
          open={showPremiumModal}
          wrapClassName={`go-premium-primary`}
          contentTitle="Want to Connect?"
          buttonTitle="Upgrade plan"
          onCancel={() => {
            setShowPremiumModal(false);
          }}
          onOk={() => {
            setShowPremiumModal(false);
          }}
        />
      )}

      {/* Added Report profile modal */}
      <ReportProfileModal
        isOpen={isReportModalOpen}
        onCancel={() => setIsReportModalOpen(false)}
        onConfirm={(selectedReason) => {
          reportProfileMutate(
            {
              reported_user_id: snippetProfileId,
              reason: selectedReason,
            },
            {
              onSuccess: () => {
                setIsReportModalOpen(false);
              },
            },
          );
        }}
      />

      {showPremiumModal && (
        <GoPremiumModal
          open={showPremiumModal}
          wrapClassName={`go-premium-primary`}
          contentTitle="Upgrade to send messages"
          buttonTitle="Upgrade plan"
          onCancel={() => {
            setShowPremiumModal(false);
          }}
          onOk={() => {
            setShowPremiumModal(false);
          }}
        />
      )}
    </>
  );
}

export default BasicProfileCard;
